import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import KOutlineBg from '../KOutlineBg';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const testimonials = [
  {
    quote: "What stood out most was Ollie's practical and considered approach. Rather than offering generic solutions, he took the time to listen and tailor his feedback to the realities of our business operation and workforce. His recommendations were clear, relevant, and achievable, and have already helped shape how we think about wellbeing across the group.",
    name: 'Chris Sanderson',
    role: 'Group Managing Director, STR Enterprises Ltd Hotels and Leisure',
    featured: true,
  },
  {
    quote: "Ollie has been a key support in our company strategy to embed team wellbeing into everything we do. He has trained our team of Mental Health First Aiders and created tailored sessions that facilitated creative input into our future wellbeing strategy.",
    name: 'Chelsea Davis',
    role: 'Group People Partner, Continuum Attractions',
  },
  {
    quote: "All the content was really interesting and thought provoking. The skills and advice gave us the confidence to have supportive conversations. What was great about Ollie was that he made a difficult topic lighter and engaging without taking away from the importance.",
    name: 'Meg Nicol',
    role: 'HR & Recruitment Manager, Darlington Building Society',
  },
  {
    quote: "It was very relaxed and light sessions with Ollie and we were made to feel very comfortable to discuss quite heavy topics. We would recommend working with Kalobrate to others in the future.",
    name: 'Lauren Fraser',
    role: 'HS&E Performance Co-ordinator, Galliford Try',
  },
  {
    quote: "What stood out most about working with Ollie was his calm, approachable manner, and the genuine understanding he showed of both our business and our people. He created an environment where staff felt comfortable sharing openly, and his insights felt relevant and grounded in the realities of our workforce.",
    name: 'Angela Thomas',
    role: 'Group Operations Manager, STR Enterprises Ltd Hotels and Leisure',
  },
  {
    quote: "Ollie was excellent from the initial meeting, through the process and the feedback. A real genuine person who wanted to learn about everyone and his relaxed personality set us all at ease. The feedback and recommendations were set out simple to us and these have helped us focus as a business on focusing where it really matters.",
    name: 'Alex Lorraine',
    role: 'General Manager, The Victoria Hotel, STR Enterprises Ltd',
  },
  {
    quote: "We have had the pleasure of working with Ollie throughout this year at BEL Engineering. His practical and impactful sessions have resulted in noticeable positive changes in behaviours within our teams. I would highly recommend Ollie to any organisation looking to support the wellbeing and engagement of their employees.",
    name: 'Claire Hopkins',
    role: 'HR Manager, BEL Engineering',
  },
];

const EvidenceSection = () => {
  const featured = testimonials.find(t => t.featured);
  const others = testimonials.filter(t => !t.featured);

  return (
    <section id="results" className="py-20 md:py-28 bg-carbon relative overflow-hidden" data-testid="evidence-section">
      <KOutlineBg />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-12 tracking-tight"
          {...fadeInUp}
        >
          WHAT CLIENTS SAY
        </motion.h2>

        {/* Featured testimonial */}
        {featured && (
          <motion.blockquote
            className="bg-white/5 border-l-4 border-golden p-8 md:p-10 mb-10"
            {...fadeInUp}
          >
            <p className="text-base md:text-lg text-white/90 leading-relaxed mb-6">
              "{featured.quote}"
            </p>
            <footer>
              <p className="text-golden font-semibold">{featured.name}</p>
              <p className="text-white/60 text-sm">{featured.role}</p>
            </footer>
          </motion.blockquote>
        )}

        {/* Other testimonials */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {others.slice(0, 3).map((t, i) => (
            <motion.blockquote
              key={t.name}
              className="bg-white/5 p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <p className="text-sm text-white/80 leading-relaxed mb-4">"{t.quote}"</p>
              <footer>
                <p className="text-golden font-semibold text-sm">{t.name}</p>
                <p className="text-white/50 text-xs">{t.role}</p>
              </footer>
            </motion.blockquote>
          ))}
        </div>

        <motion.div {...fadeInUp}>
          <Link
            to="/results"
            className="bg-golden text-carbon px-7 py-3.5 font-semibold text-sm hover:bg-golden/90 transition-colors inline-flex items-center"
            data-testid="evidence-cta"
          >
            View results and case studies
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default EvidenceSection;
